import { Button, ButtonLevel, ButtonSize, TextSize } from '@vizit/atoms';
import { Trash01Icon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import * as React from 'react';

interface DeleteCollectionDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * The id of the collection to delete.
   */
  collectionId: string | null;
  /**
   * Callback when the collection is deleted.
   */
  onDelete: (id: string) => Promise<void>;
}

function DeleteCollectionDialog(props: DeleteCollectionDialogProps) {
  const { isOpen = false, onOpenChange, onDelete, collectionId, ...other } = props;
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleOnDelete = React.useCallback(async () => {
    if (collectionId) {
      setIsDeleting(true);
      try {
        await onDelete?.(collectionId);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [onDelete, collectionId]);

  const handleClose = React.useCallback(() => {
    if (onOpenChange) {
      onOpenChange(false);
    } else {
      close();
    }
  }, [onOpenChange]);

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-4">
          <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Error} icon={Trash01Icon}>
            <Dialog.Title showEllipsis={true}>Delete Collection</Dialog.Title>
          </Dialog.Header>
          <div className="px-12">
            <Dialog.Subtitle elementType="p" size={TextSize.Md} className="text-secondary" showEllipsis={false}>
              Are you sure you want to delete this collection? This will permanently delete all images in the
              collection.
            </Dialog.Subtitle>
          </div>
          <Dialog.Footer className="grid grid-cols-2 gap-6">
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={handleClose}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              type="submit"
              level={ButtonLevel.Danger}
              onPress={handleOnDelete}
              isLoading={isDeleting}
            >
              Permanently delete
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { DeleteCollectionDialog };
export type { DeleteCollectionDialogProps };
